import { BRAND } from '@/data/content';
import { BBBSeal } from '@/components/site/SiteBits';
import { Seo } from '@/components/site/Seo';
import FinalCTA from '@/components/site/FinalCTA';
import * as Icons from 'lucide-react';

const VALUES = [
  { icon: 'Hammer', title: 'Craftsmanship', text: 'The details that don\'t show on a bid line — but show every day in the finished job.' },
  { icon: 'Handshake', title: 'Integrity', text: 'What we quote is what we do. Scope in plain language, honestly.' },
  { icon: 'Clock', title: 'Reliability', text: 'Show up when we say we will. Finish what we started.' },
  { icon: 'MessageCircle', title: 'Communication', text: 'You know what is happening and what is next — every step.' },
  { icon: 'BadgeCheck', title: 'Quality', text: 'Prep the surface, choose the right materials, and take the extra minute.' },
];

export default function About() {
  return (
    <div data-testid="page-about">
      <Seo
        title="About Ben Hardy & HearthOak Construction, LLC | Burleson, TX"
        description="Owner-led residential construction and repair based in Burleson, TX. Meet Ben Hardy and learn how HearthOak Construction approaches every project."
        path="/about"
      />
      <section className="border-b border-brand-border bg-brand-bone">
        <div className="container-x py-14 lg:py-24 grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 items-start">
          <div>
            <div className="eyebrow mb-3">About HearthOak</div>
            <h1 className="h1">Owner-led. Local. Focused on doing the job right.</h1>
            <p className="body-lg mt-6">
              HearthOak Construction exists to help people maintain and improve their homes without the frustration of unreliable contractors. The company is built around craftsmanship, integrity, reliability, communication, and quality.
            </p>
            <p className="body-lg mt-4">
              Work is approached carefully, with attention to detail and respect for the customer's home. The owner is involved in every project — start to finish — so nothing gets lost between a bid and a crew that has never met you.
            </p>
            <p className="body-lg mt-4">
              We are BBB accredited. That accreditation reflects the way we prefer to run a business — clear communication, honest scope, and standing behind our work.
            </p>
            <p className="mt-6 font-sans text-sm text-brand-muted leading-relaxed">
              HearthOak Construction, LLC formerly operated as Hardy Handy Services — the same owner and the same standard of work, carried forward under a new name.
            </p>
          </div>
          <div className="border border-brand-border bg-brand-bone p-8">
            <img src={BRAND.logoWordmark} alt="HearthOak Construction, LLC" className="w-full max-w-xs" />
            <p className="mt-8 font-hand text-4xl text-brand-maroon leading-snug text-left -rotate-1">
              {BRAND.tagline}.
            </p>
            <div className="mt-8 pt-6 border-t border-brand-border flex items-center gap-4">
              <BBBSeal size="md" testId="about-bbb-seal" />
              <div>
                <div className="font-sans text-xs uppercase tracking-[0.22em] text-brand-muted">BBB Accredited</div>
                <div className="font-serif text-lg text-brand-charcoal">Trusted local business</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="meet-the-owner" className="surface-paper border-b border-brand-border" data-testid="meet-the-owner">
        <div className="container-x py-16 lg:py-24">
          <div className="eyebrow mb-3">Meet the Owner</div>
          <h2 className="h2">Ben Hardy.</h2>

          <div className="mt-10 flow-root font-sans text-base lg:text-[17px] leading-[1.85] text-brand-charcoal/90 max-w-3xl">
            {/* Portrait floated at the top-right of the text body */}
            <figure className="relative mb-8 w-full max-w-xs sm:float-right sm:ml-10 sm:mb-6 sm:mt-1 sm:w-60 lg:w-64" data-testid="meet-the-owner-figure">
              <div className="relative">
                <span aria-hidden="true" className="absolute inset-0 translate-x-3 translate-y-3 bg-brand-maroon/90" />
                <span aria-hidden="true" className="absolute inset-0 -translate-x-2 -translate-y-2 border border-brand-border bg-brand-bone" />
                <div className="relative bg-brand-bone p-2.5 border border-brand-border shadow-[0_18px_40px_-20px_rgba(28,28,29,0.35)]">
                  <img
                    src="/brand/ben-hardy.webp"
                    alt="Ben Hardy — owner, HearthOak Construction"
                    className="block w-full h-auto"
                    loading="lazy"
                    data-testid="meet-the-owner-portrait"
                  />
                </div>
              </div>
              <figcaption className="relative mt-5 font-sans text-[11px] uppercase tracking-[0.18em] text-brand-muted text-left">
                Ben Hardy · Owner
              </figcaption>
            </figure>
            <div className="space-y-5">
              <p>I'm Ben Hardy, owner of HearthOak Construction, LLC. My background is in mechanical engineering, product design, and hands-on construction. I've spent my career understanding how things work, how they're built, and what makes them last.</p>
              <p>Before starting HearthOak, I worked in product design and development, where precision, problem-solving, and attention to detail were essential. That engineering mindset carries directly into my construction work today. I believe doing a job right means understanding more than just how to make something look good. It means knowing why it's built a certain way, choosing the right materials, and paying attention to the details that determine how well it lasts.</p>
              <p>I started HearthOak with a straightforward philosophy: do good work, treat people right, and build things that last.</p>
              <p>I've always enjoyed working with my hands and seeing an idea take shape into something real. There's a particular satisfaction in stepping back at the end of a project, seeing the details come together, and knowing the work was done right. Even better is seeing a homeowner genuinely excited about the finished result. That's the part of this work that makes it personal to me.</p>
              <p>To me, a home is more than just a building. It's where you spend your time, make memories, and take pride in what you've worked hard to create. Whether we're making a small repair, restoring something that's seen better days, or bringing a new vision to life, our goal is to make your home a place you're proud of. We value the opportunity to improve your property, not just in appearance, but in quality, functionality, and long-term value.</p>
              <p>I'm not interested in cutting corners or selling customers work they don't need. I believe in honest communication, thoughtful solutions, and craftsmanship that reflects the pride we at HearthOak take in our work. Every project is an opportunity to leave someone's home better than we found it.</p>
              <p className="pt-2 font-serif text-xl text-brand-charcoal leading-snug">That's what HearthOak is built around, and it's the meaning behind our mission: <span className="text-brand-maroon">Making Houses Homes.</span></p>
            </div>
          </div>
        </div>
      </section>

      <section className="surface-paper border-b border-brand-border">
        <div className="container-x py-16 lg:py-24">
          <div className="max-w-2xl">
            <h2 className="h2">What we care about.</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 border-t border-l border-brand-border">
            {VALUES.map((v) => {
              const Ico = Icons[v.icon] || Icons.Hammer;
              return (
                <div key={v.title} className="p-7 border-r border-b border-brand-border bg-brand-bone">
                  <Ico size={22} className="text-brand-maroon" strokeWidth={1.5} />
                  <div className="mt-4 font-serif text-xl text-brand-charcoal">{v.title}</div>
                  <p className="mt-2 font-sans text-sm text-brand-muted leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <FinalCTA />
    </div>
  );
}
